// @ts-nocheck
/**
 * CardDexScreen - 図鑑メイン画面
 *
 * 図鑑システムのルーターとして機能し、
 * 各サブ画面への遷移を管理する
 */

import React, { createContext, useContext, useEffect, useRef } from 'react';
import { CardDexProvider, useCardDex, DEX_VIEWS } from '../hooks/useCardDex';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { screenStyles, navStyles, COLORS } from '../styles/cardDexStyles';

// サブ画面コンポーネント
import CardListScreen from './list/CardListScreen';
import CardDetailScreen from './detail/CardDetailScreen';
import ProgressScreen from './progress/ProgressScreen';
// import AchievementScreen from './achievements/AchievementScreen';
// import StatisticsScreen from './statistics/StatisticsScreen';

// Phase 2.5: 素材図鑑
import MaterialListScreen from './materials/MaterialListScreen';
import MaterialDetailScreen from './materials/MaterialDetailScreen';

// ========================================
// カードデータコンテキスト
// ========================================

const CardDataContext = createContext(null);

export const useCardData = () => {
  const context = useContext(CardDataContext);
  if (!context) {
    throw new Error('useCardData must be used within CardDexScreen');
  }
  return context;
};

// ========================================
// メインタブナビゲーション
// ========================================

// タブ定義
const TABS = [
  { id: DEX_VIEWS.LIST, label: '図鑑', icon: '📖' },
  { id: DEX_VIEWS.MATERIALS, label: '素材', icon: '🧪' },
  { id: DEX_VIEWS.PROGRESS, label: '進捗', icon: '📊' },
  // Phase 3で追加
  // { id: DEX_VIEWS.ACHIEVEMENTS, label: '実績', icon: '🏆' },
  // { id: DEX_VIEWS.STATISTICS, label: '統計', icon: '📈' },
];

// PC用タブ
const MainTabs = () => {
  const { currentView, navigateToList, navigateToProgress, navigateToMaterials } = useCardDex();
  const isMobile = useIsMobile();

  const handleTabClick = (tabId) => {
    switch (tabId) {
      case DEX_VIEWS.LIST:
        navigateToList();
        break;
      case DEX_VIEWS.MATERIALS:
        navigateToMaterials();
        break;
      case DEX_VIEWS.PROGRESS:
        navigateToProgress();
        break;
      default:
        break;
    }
  };

  // 詳細画面またはモバイルではタブを表示しない
  if (currentView.view === DEX_VIEWS.DETAIL || currentView.view === DEX_VIEWS.MATERIAL_DETAIL || isMobile) {
    return null;
  }

  return (
    <div style={navStyles.tabContainer}>
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => handleTabClick(tab.id)}
          style={{
            ...navStyles.tab,
            ...(currentView.view === tab.id ? navStyles.tabActive : {}),
          }}
        >
          {tab.icon} {tab.label}
        </button>
      ))}
    </div>
  );
};

// モバイル用フッタータブ
const FooterTabs = () => {
  const { currentView, navigateToList, navigateToProgress, navigateToMaterials } = useCardDex();
  const isMobile = useIsMobile();

  const handleTabClick = (tabId) => {
    switch (tabId) {
      case DEX_VIEWS.LIST:
        navigateToList();
        break;
      case DEX_VIEWS.MATERIALS:
        navigateToMaterials();
        break;
      case DEX_VIEWS.PROGRESS:
        navigateToProgress();
        break;
      default:
        break;
    }
  };

  // PC または詳細画面では表示しない
  if (!isMobile || currentView.view === DEX_VIEWS.DETAIL || currentView.view === DEX_VIEWS.MATERIAL_DETAIL) {
    return null;
  }

  return (
    <div style={navStyles.footerTabNavigation}>
      {TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => handleTabClick(tab.id)}
          style={{
            ...navStyles.footerTab,
            ...(currentView.view === tab.id ? navStyles.footerTabActive : {}),
          }}
        >
          <span style={navStyles.footerTabIcon}>{tab.icon}</span>
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// ========================================
// 画面ルーター
// ========================================

const ScreenRouter = () => {
  const { currentView } = useCardDex();

  switch (currentView.view) {
    case DEX_VIEWS.LIST:
      return <CardListScreen />;

    case DEX_VIEWS.DETAIL:
      return <CardDetailScreen cardId={currentView.params?.cardId} />;

    case DEX_VIEWS.PROGRESS:
      return <ProgressScreen />;

    // Phase 2.5: 素材図鑑
    case DEX_VIEWS.MATERIALS:
      return <MaterialListScreen />;

    case DEX_VIEWS.MATERIAL_DETAIL:
      return <MaterialDetailScreen materialId={currentView.params?.materialId} />;

    // Phase 3で追加
    // case DEX_VIEWS.ACHIEVEMENTS:
    //   return <AchievementScreen />;

    // case DEX_VIEWS.STATISTICS:
    //   return <StatisticsScreen />;

    default:
      return <CardListScreen />;
  }
};

// ========================================
// 図鑑画面本体
// ========================================

const CardDexContent = ({ onBack }) => {
  const { currentView, goBack, viewHistory } = useCardDex();
  const isMobile = useIsMobile();

  // popstateからの遷移かどうかを追跡（二重履歴追加防止）
  const isPopstateTransitionRef = useRef(false);
  const prevViewRef = useRef(currentView.view);

  // 画面変更時に自動でhistory追加
  useEffect(() => {
    // popstateからの遷移の場合は履歴を追加しない
    if (isPopstateTransitionRef.current) {
      isPopstateTransitionRef.current = false;
      prevViewRef.current = currentView.view;
      return;
    }

    // 画面が変わった場合にのみ履歴を追加
    if (prevViewRef.current !== currentView.view) {
      window.history.pushState(
        { screen: 'cardDex', cardDexView: currentView.view, timestamp: Date.now() },
        '',
        ''
      );
      prevViewRef.current = currentView.view;
    }
  }, [currentView.view]);

  // popstateイベントハンドラ（戻るボタン押下時）
  useEffect(() => {
    const handlePopState = (event) => {
      const historyState = event.state;

      // CardDex以外の画面への遷移の場合は無視（親のハンドラに任せる）
      if (!historyState?.cardDexView && historyState?.screen !== 'cardDex') {
        return;
      }

      // 図鑑内に履歴がある場合は内部で戻る
      if (viewHistory.length > 0) {
        isPopstateTransitionRef.current = true;
        goBack();
        return;
      }

      // 履歴がない場合（リスト画面）は親のハンドラに任せる
      // 何もしないでイベントを伝播させる
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [viewHistory, goBack]);

  const handleBack = () => {
    // まず図鑑内の履歴を戻る
    const wentBack = goBack();
    // 履歴がなければメニューに戻る
    if (!wentBack && onBack) {
      onBack();
    }
  };

  return (
    <div
      style={{
        ...screenStyles.container,
        ...(isMobile ? screenStyles.containerMobile : screenStyles.containerDesktop),
      }}
    >
      {/* ヘッダー */}
      <div style={isMobile ? screenStyles.headerMobile : screenStyles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px' }}>
          <button
            onClick={handleBack}
            style={{
              ...navStyles.backButton,
              padding: isMobile ? '8px 14px' : '10px 20px',
              fontSize: isMobile ? '12px' : '14px',
            }}
          >
            ◀ 戻る
          </button>
          <h1
            style={{
              ...screenStyles.title,
              ...(isMobile ? screenStyles.titleMobile : {}),
            }}
          >
            📚 カード図鑑
          </h1>
        </div>
      </div>

      {/* メインタブ（PC用） */}
      <MainTabs />

      {/* 画面コンテンツ */}
      <ScreenRouter />

      {/* フッタータブ（モバイル用） */}
      <FooterTabs />
    </div>
  );
};

// ========================================
// エクスポート（プロバイダー付き）
// ========================================

const CardDexScreen = ({ onBack, allCards = [], playerData = null, updatePlayerData = null }) => {
  // playerDataからコレクション情報を抽出
  const collection = playerData?.collection || [];

  return (
    <CardDataContext.Provider value={{ allCards, collection, playerData, updatePlayerData }}>
      <CardDexProvider>
        <CardDexContent onBack={onBack} />
      </CardDexProvider>
    </CardDataContext.Provider>
  );
};

export default CardDexScreen;
export { DEX_VIEWS };
